export enum PRIMARY_COLORS {
  DARK_GRAY = "#3B3B3B",
  GHOST_WHITE = "#F5FCFF",
  PATTENS_BLUE = "#D5E2EA",
  CERULEAN = "#00649C",
  GREY_CHATEAU = "#98A0A7",
}
// Documentary Editorial Badge Colors - Warm, earthy tones
// Keys match both camelCase (legacy) and display names from database
export const CATEGORIES: Record<string, string> = {
  // Legacy camelCase keys
  climate: "bg-[#D1FAE5]", // Soft green - olive family
  employment: "bg-[#FEE4CF]", // Warm peach - terracotta family
  education: "bg-[#FDE7D9]", // Soft apricot
  addiction: "bg-[#E9F5D8]", // Light sage
  povertyAndHunger: "bg-[#DCFCE7]", // Pale green
  insecurityAndInstability: "bg-[#F5E6D3]", // Warm sand
  health: "bg-[#D5F5F0]", // Soft teal
  injusticeAndDiscrimination: "bg-[#FFEDD5]", // Light ochre
  corruption: "bg-[#E0E7FF]", // Soft lavender
  disunityAndDivision: "bg-[#FEF3C7]", // Warm cream yellow
  // Display name keys (from database)
  "Education": "bg-[#FDE7D9]", // Soft apricot
  "Technology": "bg-[#E0E7FF]", // Soft lavender
  "Infrastructure": "bg-[#E7E5E4]", // Stone
  "Climate & Environment": "bg-[#D1FAE5]", // Soft green
  "Health & Wellness": "bg-[#D5F5F0]", // Soft teal
  "Poverty & Hunger": "bg-[#DCFCE7]", // Pale green
  "Security & Conflict": "bg-[#F5E6D3]", // Warm sand
  "Justice & Equality": "bg-[#FFEDD5]", // Light ochre
  "Politics & Governance": "bg-[#E0E7FF]", // Soft lavender
  "Employment & Economy": "bg-[#FEE4CF]", // Warm peach
  "Unity & Division": "bg-[#FEF3C7]", // Warm cream yellow
};

// Category accent colors for card left borders and pill highlights (more saturated versions)
export const CATEGORY_ACCENT_COLORS: Record<string, string> = {
  // Legacy camelCase keys
  climate: "#4ADE80", // Green-400
  employment: "#FB923C", // Orange-400
  education: "#F87171", // Red-400
  addiction: "#A3E635", // Lime-400
  povertyAndHunger: "#34D399", // Emerald-400
  insecurityAndInstability: "#D97706", // Amber-600
  health: "#2DD4BF", // Teal-400
  injusticeAndDiscrimination: "#FBBF24", // Amber-400
  corruption: "#818CF8", // Indigo-400
  disunityAndDivision: "#FACC15", // Yellow-400
  // Display name keys (from database)
  "Education": "#F87171", // Red-400
  "Technology": "#818CF8", // Indigo-400
  "Infrastructure": "#78716C", // Stone-500
  "Climate & Environment": "#4ADE80", // Green-400
  "Health & Wellness": "#2DD4BF", // Teal-400
  "Poverty & Hunger": "#34D399", // Emerald-400
  "Security & Conflict": "#D97706", // Amber-600
  "Justice & Equality": "#FBBF24", // Amber-400
  "Politics & Governance": "#818CF8", // Indigo-400
  "Employment & Economy": "#FB923C", // Orange-400
  "Unity & Division": "#FACC15", // Yellow-400
};

// Documentary Editorial fallback badge colors
export const CATEGORIES_COLORS = [
  "bg-[#FEE4CF]", // Terracotta light
  "bg-[#E9F5D8]", // Olive light
  "bg-[#FFEDD5]", // Ochre light
  "bg-[#F5E6D3]", // Sand
  "bg-[#DCFCE7]", // Sage
  "bg-[#D5F5F0]", // Teal soft
  "bg-[#FDE7D9]", // Apricot
  "bg-[#FEF3C7]", // Cream yellow
  "bg-[#D1FAE5]", // Mint
  "bg-[#E7E5E4]", // Stone
  "bg-[#E0E7FF]", // Lavender soft
  "bg-[#FCE7F3]", // Rose soft
];

export enum CATEGORIES_GRADIENTS {
  povertyAndHunger = "from-[#FBC12B] to-[#F26437]",
  health = "from-[#DC88DE] via-[#8159D7] to-[#9BB6FB]",
  insecurityAndInstability = "from-[#9EDE88] via-[#80C866] to-[#1B8B09]",
  education = "from-[#E29AE2] to-[#D059AC]",
  disunityAndDivision = "from-[#ED8B8B] via-[#EC7676] to-[#A92A19]",
  climate = "from-[#379835] to-[#368535]",
  employment = "from-[#06A0AA] to-[#64D4DB]",
  addiction = "from-[#4DE007] to-[#E4E832]",
  injusticeAndDiscrimination = "from-[#A4BDFC] to-[#96AFF0]",
  corruption = "from-[#FEE382] to-[#F3BF81]",
}

export const BADGE_COLORS = [
  "from-[#FBC12B] to-[#F26437]",
  "from-[#DC88DE] via-[#8159D7] to-[#9BB6FB]",
  "from-[#9EDE88] via-[#80C866] to-[#1B8B09]",
  "from-[#E29AE2] to-[#D059AC]",
  "from-[#ED8B8B] via-[#EC7676] to-[#A92A19]",
  "from-[#379835] to-[#368535]",
  "from-[#06A0AA] to-[#64D4DB]",
  "from-[#4DE007] to-[#E4E832]",
  "from-[#A4BDFC] to-[#96AFF0]",
  "from-[#FEE382] to-[#F3BF81]",
];

export enum SOLUTION_CATEGORIES_GRADIENTS {
  money = "from-[#DC88DE] via-[#8159D7] to-[#9BB6FB]",
  teaching = "from-[#FBC12B]  to-[#F26437]",
  studying = "from-[#9EDE88] via-[#80C866] to-[#1B8B09]",
}

export enum LOADER_SIZES {
  tiny = 50,
  small = 100,
  medium = 300,
  large = 600,
  huge = 900,
}

export enum ACTIVITY_TYPES {
  COMMENT_LIKE = "commentLike",
  LIKE = "like",
  DISLIKE = "dislike",
  CREATE_SOLUTION = "createSolution",
  CREATE_ACTION_GROUP = "createActionGroup",
  REQUEST_JOIN_GROUP = "requestJoinGroup",
  APPROVE_JOIN_GROUP = "approveJoinGroup",
  DECLINE_JOIN_GROUP = "declineJoinGroup",
  LEAVE_ACTION_GROUP = "leaveActionGroup",
  DELETE_ACTION_GROUP = "deleteActionGroup",
  RATE_ACTION_GROUP = "rateActionGroup",
  RATE_ISSUE = "rateIssue",
  DELETE_ISSUE_RATE = "deleteIssueRate",
  COMMENT = "comment",
  REPLY = "reply",
  CASCADE_SOFT_DELETE_ISSUE = "cascadeSoftDeleteIssue",
  CASCADE_SOFT_RESTORE_ISSUE = "cascadeSoftRestoreIssue",
  RATE_MOTIVATIONAL = "rateMotivational",
  RATE_SOLUTION = "rateSolution",
}

export enum ENTITY_TYPES {
  ISSUE = "issue",
  SOLUTION = "solution",
  ACTION_GROUP = "actionGroup",
  ACTION_GROUP_USER = "actionGroupUser",
  COMMENT = "comment",
  ACTION_GROUP_RATING = "rating",
  ISSUE_RATING = "issueRating",
  REPLY = "reply",
  MOTIVATIONAL = "motivational",
  MOTIVATIONAL_RATING = "motivationalRating",
  USER = "user",
  SOLUTION_RATING = "solutionRating",
}
export const SKILLS_BADGE_COLORS = [
  "text-red-500  focus:bg-red-500",
  "text-yellow-500  focus:bg-yellow-500",
  "text-green-500  focus:bg-green-500",
  "text-blue-500  focus:bg-blue-500",
  "text-indigo-500  focus:bg-indigo-500",
];

export enum CONTENT_QUALITY_LEVELS {
  USELESS = "Useless",
  POOR = "Poor",
  LOW = "Low",
  MEDIUM = "Medium",
  GOOD = "Good",
  HIGH = "High",
}
